/**
 * Générateur — Il connaît la définition de la racine carrée d'un nombre positif.
 * Attendu ID : 140 | Niveau : 4ème | Domaine : Nombres (desc 11, O5)
 *
 * Source doc prof (Cluster 1, O5) : définition de √a, valeurs √(carré parfait),
 * V/F (« √16 vaut 4 ou -4 » faux, √16 = 4 car 4² = 16 vrai, √(−9) n'existe pas, (√5)² = 5),
 * relier. Plan de cartes validé 2026-07-14, revu 2026-09-22 :
 *
 *   Carte « def » — quelle phrase définit √K ? (qcm_unique, valeur concrète)
 *   Carte « valeur » — √(carré parfait) (texte/saisie)
 *   Carte « relier » — √4 ↔ 2, √36 ↔ 6… (relier, leurres carré / moitié / racine voisine)
 *   Carte « vf » — pièges classiques (tableau_vf, énoncés paramétrés)
 *
 * Tout par construction (carrés parfaits). Racines en KaTeX partout (barre du
 * radical couvrant tout le radicande — la notation fidèle fait partie de
 * l'attendu ; arbitrage 2026-07-26), par les helpers racineTex et tex de
 * generators/utils.js (revue 2026-09-23 : plus aucun `$…$` écrit à la main ;
 * le helper garantit que chaque fragment commence par une commande LaTeX,
 * exigence de la regex stricte de RichTextBlockEleve). Jamais de math dans un
 * trou : les réponses saisies restent des nombres nus.
 *
 * Revue 2026-09-22 : deux cartes SUPPRIMÉES. « entiere » (lesquels ont une
 * racine entière ?) était la copie stricte de la carte « reconnaitre » de
 * l'attendu 139 (mêmes carrés, mêmes non-carrés, même structure de deux ou
 * trois bons sur six), seule la consigne changeait : 139 garde ce QCM, 140
 * garde « def » comme QCM. « geo » (côté d'un carré d'aire donnée) était la
 * carte « valeur » habillée en géométrie, situation qui appartient à
 * l'attendu 151. Sur les cartes gardées : « def » propose le piège du signe
 * (« le nombre négatif dont le carré… ») à la place d'un distracteur qui
 * valait, comme les deux autres, la moitié du carré, et ses trois distracteurs
 * ont désormais trois valeurs distinctes (opposé de la racine, moitié, double) ;
 * « relier » reçoit un leurre « racine voisine » (dans 74 % des tirages, les
 * deux bonnes cibles étaient les deux plus petits nombres affichés) ; « vf »
 * perd la ligne « √K = r », déduite de la justification tirée avec elle une
 * fois sur deux, et les deux lignes sur la racine d'un négatif portent des
 * valeurs différentes ; les parenthèses explicatives deviennent des phrases.
 *
 * Revue 2026-09-23 : typographie. Les onze fragments `$\sqrt{…}$` écrits à la
 * main (énoncés, sources du relier, lignes vrai/faux) passent par racineTex et
 * tex ; chaînes produites identiques, aucun changement d'affichage.
 *
 * Portée 2026-09-23 (question prof « c'est quoi ce symbole ? ») : la ligne
 * piège de « vf » s'écrivait « √K = ± r ». Le symbole ± n'est ni dans
 * l'intitulé, ni dans les exemples de réussite, ni dans le cours de 4e (il
 * arrive en 3e avec les équations x² = a) : l'élève qui ne le connaît pas ne
 * juge pas la phrase, il devine. Elle dit désormais « √S vaut s ou -s », en
 * toutes lettres. L'erreur visée est la même et elle est la bonne : s et -s ont
 * tous deux S pour carré, mais le SYMBOLE désigne un seul nombre, le positif,
 * sinon on ne pourrait pas calculer avec. Deux corrections sont venues avec :
 * la ligne « √-p = -p » est SUPPRIMÉE (elle écrivait une égalité sur un objet
 * qui n'existe pas et doublonnait « √-m existe »), et la ligne piège porte
 * maintenant sa propre valeur s, distincte de r, p et m : avec la même, la
 * justification « √K = r car r² = K » donnait la réponse du piège, et les deux
 * sortaient ensemble trois fois sur cinq (vu au dump).
 *
 * Composition 2026-09-23 (règle prof) : « vf » passe de cinq lignes à quatre,
 * sinon c'est trop long à lire ; la banque compte cinq lignes, trois vraies et
 * deux fausses, tirer quatre sur cinq laisse toujours au moins deux vraies et
 * une fausse. « relier »
 * vérifié et inchangé : deux sources pour cinq ou six cibles, donc déjà moins
 * d'éléments à gauche qu'à droite et trois au plus à gauche.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeRelier, makeVraiFaux, racineTex, tex,
} from '../../../utils';
import { CARRES } from './nombres';

const ATTENDU_ID = 140;

const L_DEF = 'Connaître la définition de la racine carrée';
const L_VALEUR = 'Calculer la racine carrée d\'un carré parfait';
const L_RELIER = 'Associer racine carrée et valeur';
const L_VF = 'Éviter les pièges sur la racine carrée';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ Carte « def » — quelle phrase définit √K ? ═════════

// Chaque variante : la définition juste et trois définitions fausses de valeurs
// distinctes, dont le piège réel de l'attendu, le signe (« le nombre négatif
// dont le carré… » : −√K), et deux confusions, avec le double (K/2) et avec la
// moitié (2K).
const DEF_VARIANTES = [
    {
        bon: (K) => `C'est le nombre positif dont le carré est égal à ${K}.`,
        faux: [
            (K) => `C'est le nombre négatif dont le carré est égal à ${K}.`,
            (K) => `C'est le nombre positif dont le double est égal à ${K}.`,
            (K) => `C'est le nombre positif dont la moitié est égale à ${K}.`,
        ],
    },
    {
        bon: (K) => `C'est le nombre positif dont le carré vaut ${K}.`,
        faux: [
            (K) => `C'est le nombre négatif dont le carré vaut ${K}.`,
            (K) => `C'est le nombre positif dont la moitié vaut ${K}.`,
            (K) => `C'est le nombre positif dont le double vaut ${K}.`,
        ],
    },
];

const genDefQcm = () => {
    const n = randInt(2, 12);        // 11 valeurs × 2 formulations ≥ 22 (unicité)
    const K = CARRES[n];
    const v = randomChoice(DEF_VARIANTES);
    const correct = v.bon(K);
    const choix = [correct, ...v.faux.map(f => f(K))];
    const texte = `<p><b>Quelle phrase définit correctement ${racineTex(K)}, la racine carrée de ${K} ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_DEF));
    payload._meta.params = { K, distracteurs: ['signe', 'double', 'moitie'] };
    return payload;
};

// ═════════ Carte « valeur » — √(carré parfait) ═════════

const VALEUR_TEMPLATES = [
    (K) => `<p><b>Calcule ${racineTex(K)}, la racine carrée de ${K}.</b></p>`,
    (K) => `<p><b>Quelle est la racine carrée de ${K} ?</b></p>`,
    (K) => `<p><b>Donne la valeur de ${racineTex(K)}.</b></p>`,
];

const genValeurSaisie = () => {
    const n = randInt(1, 12);        // 12 valeurs × 3 formulations (unicité)
    const K = CARRES[n];
    const payload = makeSaisie(String(n), randomChoice(VALEUR_TEMPLATES)(K), opts(L_VALEUR));
    payload._meta.params = { n, K };
    return payload;
};

// ═════════ Carte « relier » — √K ↔ n ═════════

const RACINES = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const genRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : gabarit 2 sources / 4-6 cibles.
    // √1 exclu (racine, carré et radicande y valent tous 1). Leurres :
    //   · carre_confusion : le radicande lui-même (« 81 » pour √81) — l'élève
    //     qui confond racine et carré, ou qui apparie le nombre vu ;
    //   · moitie : la moitié du radicande quand un K est pair (« 8 » pour
    //     √16) — la confusion racine = moitié ;
    //   · racine_voisine (revue 2026-09-22) : un entier de 2 à 12 qui n'est la
    //     racine d'aucune source, voisin d'une bonne cible quand c'est possible.
    //     Sans lui, les leurres valaient le carré ou sa moitié, donc dépassaient
    //     les racines : dans 74 % des tirages, les deux bonnes cibles étaient les
    //     deux plus petits nombres affichés, reliables sans aucun calcul.
    // Contrainte : ne jamais tirer ensemble n et n² comme sources (√4 avec
    // √16 → le leurre « 4 » doublerait la cible « 4 ») ; toutes cibles
    // distinctes, leurres compris (dédup systématique).
    let ns;
    do {
        ns = shuffle(RACINES).slice(0, 2);
    } while (ns[0] === ns[1] * ns[1] || ns[1] === ns[0] * ns[0]);
    const pairs = ns.map(n => ({ source: racineTex(CARRES[n]), target: String(n) }));

    const pris = new Set(ns.map(String));
    const leurres = [];
    for (const n of ns) {
        const K = CARRES[n];
        if (pris.has(String(K))) continue;
        pris.add(String(K));
        leurres.push({ target: String(K), code: 'carre_confusion' });
    }
    for (const n of ns) {
        const K = CARRES[n];
        if (K % 2 !== 0 || pris.has(String(K / 2))) continue;
        pris.add(String(K / 2));
        leurres.push({ target: String(K / 2), code: 'moitie' });
        break;
    }
    const libre = (v) => v >= 2 && v <= 12 && !pris.has(String(v));
    const voisins = ns.flatMap(n => [n - 1, n + 1]).filter(libre);
    const candidats = voisins.length ? voisins : RACINES.filter(libre);
    if (candidats.length) {
        const v = randomChoice(candidats);
        pris.add(String(v));
        leurres.push({ target: String(v), code: 'racine_voisine' });
    }

    const payload = makeRelier(pairs,
        '<p><b>Relie chaque racine carrée à sa valeur.</b> Attention : certains nombres ne correspondent à aucune racine.</p>',
        { ...opts(L_RELIER), cibles_leurres: leurres });
    payload._meta.params = {
        ns,
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ═════════ Carte « vf » — pièges (énoncés paramétrés) ═════════

const genVfTableau = () => {
    const r = randInt(3, 12);
    const K = r * r;
    const m = randomChoice([2, 3, 5, 7, 10]);        // non carré parfait usuel
    // p ≠ m : les deux lignes sur la racine d'un négatif portent des valeurs
    // différentes, sinon l'une se déduit de l'autre ; p ≠ r : la ligne √p² = p
    // ne répète pas la valeur de la justification.
    const p = randomChoice([2, 3, 4, 5, 6, 7, 8, 9].filter(x => x !== m && x !== r));
    // s ≠ r, p, m : la ligne du piège (« √s² vaut s ou -s ») ne doit PAS porter
    // la valeur de la justification (« √K = r car r² = K »), sinon la seconde
    // donne la réponse de la première. Les deux sortaient ensemble trois fois
    // sur cinq (vu au dump, 2026-09-23).
    const s = randomChoice([3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter(x => x !== r && x !== p && x !== m));
    const S = s * s;
    const banque = [
        // Le piège de la carte : l'élève sait que r et -r ont tous deux K pour
        // carré, et en conclut que la racine vaut « l'un ou l'autre ». C'est
        // faux, le symbole désigne UN seul nombre, le positif. Écrit en toutes
        // lettres et non avec « ± » (question prof 2026-09-23) : le symbole
        // n'est ni dans l'intitulé, ni dans les exemples de réussite, ni dans
        // le cours de 4e, il arrive en 3e avec les équations x² = a. Un élève
        // qui ne le connaît pas ne juge pas la phrase, il devine.
        { txt: `${racineTex(S)} vaut ${s} ou ${tex(`{-${s}}`)}`, vrai: false },
        { txt: tex(`\\sqrt{${K}} = ${r} \\text{ car } ${r}^2 = ${K}`), vrai: true },      // justification correcte
        { txt: `${racineTex(`-${m}`)} existe`, vrai: false },                              // racine d'un négatif
        { txt: tex(`\\left(\\sqrt{${m}}\\right)^2 = ${m}`), vrai: true },                 // carré de la racine
        { txt: tex(`\\sqrt{${p * p}} = ${p}`), vrai: true },
        // RETIRÉE le 2026-09-23 : « √-7 = -7 ». Elle écrivait une égalité sur un
        // objet qui n'existe pas, ce qui embrouille au lieu de tester, et elle
        // faisait doublon avec la ligne « √-m existe », qui dit la même chose
        // proprement.
    ];
    // Quatre lignes (règle prof 2026-09-23 : quatre au plus) parmi 3 vraies + 2
    // fausses : tirer 4 sur 5 laisse toujours au moins deux vraies et une fausse.
    const sel = shuffle(banque).slice(0, 4);
    const payload = makeVraiFaux(sel.map(x => x.txt), sel.map(x => (x.vrai ? 0 : 1)),
        `<p>Le symbole ${tex('\\sqrt{\\;}')} désigne la racine carrée.</p><p><b>Vrai ou faux ?</b></p>`, opts(L_VF));
    payload._meta.params = { K, r, m, p, s, S };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    def_qcm_unique: genDefQcm,
    valeur_texte: genValeurSaisie,
    relier_relier: genRelier,
    vf_tableau_vf: genVfTableau,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', relier: 'Relier', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'def', label: L_DEF, formatsList: buildFormats('def') },
    { id: 'valeur', label: L_VALEUR, formatsList: buildFormats('valeur') },
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
